import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import API from "../api/client";
import TopBar from "../components/layout/TopBar";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Search, X, ArrowUpDown, List, LayoutGrid, GripVertical } from "lucide-react";
import { format, isToday, isBefore, startOfDay } from "date-fns";
import { toast } from "sonner";

const STATUS_COLORS = {
  New: "bg-blue-50 text-blue-700 border-blue-200",
  Open: "bg-sky-50 text-sky-700 border-sky-200",
  "In Progress": "bg-amber-50 text-amber-700 border-amber-200",
  Waiting: "bg-orange-50 text-orange-700 border-orange-200",
  Completed: "bg-green-50 text-green-700 border-green-200",
  Archived: "bg-gray-100 text-gray-500 border-gray-200",
};

const PRIORITY_COLORS = {
  Low: "bg-gray-50 text-gray-600 border-gray-200",
  Normal: "bg-blue-50 text-blue-600 border-blue-200",
  High: "bg-orange-50 text-orange-600 border-orange-200",
  Urgent: "bg-red-50 text-red-600 border-red-200",
};

export default function TaskListPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [showCreate, setShowCreate] = useState(searchParams.get("create") === "true");
  const [filters, setFilters] = useState({
    search: searchParams.get("search") || "",
    status: "",
    priority: "",
    owner_id: "",
    group_id: "",
    my_tasks: false,
  });
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("taskViewMode") || "list");
  const [draggedTask, setDraggedTask] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const BASE_COL_WIDTH = 288;
  const [columnWidth, setColumnWidth] = useState(() => {
    const saved = localStorage.getItem("kanbanColumnWidth");
    return saved ? parseInt(saved, 10) : BASE_COL_WIDTH;
  });
  const resizingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(BASE_COL_WIDTH);

  // Create form
  const [form, setForm] = useState({ title: "", description: "", notes: "", owner_id: "", group_id: "", due_date: "", priority: "Normal", flag_id: "" });

  const fetchTasks = useCallback(async () => {
    const params = new URLSearchParams();
    if (filters.search) params.set("search", filters.search);
    if (filters.status) params.set("status", filters.status);
    if (filters.priority) params.set("priority", filters.priority);
    if (filters.owner_id) params.set("owner_id", filters.owner_id);
    if (filters.group_id) params.set("group_id", filters.group_id);
    if (filters.my_tasks) params.set("my_tasks", "true");
    params.set("sort_by", sortBy);
    params.set("sort_order", sortOrder);
    const { data } = await API.get(`/tasks?${params}`);
    setTasks(data);
  }, [filters, sortBy, sortOrder]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  useEffect(() => {
    API.get("/users").then(r => setUsers(r.data)).catch(() => {});
    API.get("/groups").then(r => setGroups(r.data)).catch(() => {});
    API.get("/settings/flags").then(r => setFlags(r.data)).catch(() => {});
  }, []);

  const [flags, setFlags] = useState([]);

  const handleCreate = async () => {
    if (!form.title.trim()) return;
    try {
      const { data } = await API.post("/tasks", {
        ...form,
        owner_id: form.owner_id || user.id,
        group_id: form.group_id || null,
        due_date: form.due_date || null,
        flag_id: form.flag_id || null,
      });
      toast.success(`Task ${data.task_number} created`);
      setShowCreate(false);
      setForm({ title: "", description: "", notes: "", owner_id: "", group_id: "", due_date: "", priority: "Normal", flag_id: "" });
      fetchTasks();
    } catch (err) {
      toast.error("Failed to create task");
    }
  };

  const toggleSort = (field) => {
    if (sortBy === field) setSortOrder(o => o === "asc" ? "desc" : "asc");
    else { setSortBy(field); setSortOrder("asc"); }
  };

  const changeViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem("taskViewMode", mode);
  };

  // Drag and drop handlers
  const handleDragStart = (e, task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", task.id);
    e.currentTarget.style.opacity = "0.5";
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    setDraggedTask(null);
    setDragOverColumn(null);
  };

  const handleDragOver = (e, status) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverColumn(status);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    setDragOverColumn(null);
    if (!draggedTask || draggedTask.status === newStatus) return;
    try {
      await API.put(`/tasks/${draggedTask.id}`, { status: newStatus });
      toast.success(`Moved to ${newStatus}`);
      fetchTasks();
    } catch {
      toast.error("Failed to update status");
    }
    setDraggedTask(null);
  };

  const resetFilters = () => setFilters({ search: "", status: "", priority: "", owner_id: "", group_id: "", my_tasks: false });

  // Card color based on due date status
  const getCardBg = (task) => {
    if (task.status === "Completed" || task.status === "Archived") return "#F8F9FA";
    if (task.due_date) {
      const today = startOfDay(new Date());
      const due = new Date(task.due_date + "T00:00:00");
      if (isBefore(due, today)) return "#FFACAB"; // Past due - Blush
      if (isToday(due)) return "#FFD7B3"; // Due today - Peach
    }
    if (task.status === "New") return "#F8F9FA"; // New - Off White
    if (["Open", "In Progress", "Waiting"].includes(task.status)) return "#E9FFF3"; // Mint
    return "#F8F9FA";
  };

  // Column resize handlers
  const handleResizeStart = (e) => {
    e.preventDefault();
    resizingRef.current = true;
    startXRef.current = e.clientX;
    startWidthRef.current = columnWidth;
    document.addEventListener("mousemove", handleResizeMove);
    document.addEventListener("mouseup", handleResizeEnd);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const handleResizeMove = useCallback((e) => {
    if (!resizingRef.current) return;
    const delta = e.clientX - startXRef.current;
    const newWidth = Math.max(180, Math.min(600, startWidthRef.current + delta));
    setColumnWidth(newWidth);
  }, []);

  const handleResizeEnd = useCallback(() => {
    resizingRef.current = false;
    document.removeEventListener("mousemove", handleResizeMove);
    document.removeEventListener("mouseup", handleResizeEnd);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    setColumnWidth(w => {
      localStorage.setItem("kanbanColumnWidth", String(w));
      return w;
    });
  }, [handleResizeMove]);

  // Font scale factor
  const scale = columnWidth / BASE_COL_WIDTH;
  const scaledFont = (base) => Math.max(8, Math.round(base * scale));

  const hasFilters = filters.search || filters.status || filters.priority || filters.owner_id || filters.group_id || filters.my_tasks;

  const KANBAN_STATUSES = ["New", "Open", "In Progress", "Waiting", "Completed"];
  const KANBAN_HEADER_COLORS = {
    New: "bg-blue-500", Open: "bg-sky-500", "In Progress": "bg-amber-500", Waiting: "bg-orange-500", Completed: "bg-green-500"
  };

  return (
    <>
      <TopBar title="Tasks" onCreateTask={() => setShowCreate(true)} />
      <div className="flex-1 overflow-y-auto p-6" data-testid="task-list-page">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-[#686B74]" />
            <Input
              data-testid="task-search-input"
              placeholder="Search tasks..."
              value={filters.search}
              onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
              className="pl-8 h-8 text-sm"
            />
          </div>
          <Select value={filters.status} onValueChange={v => setFilters(f => ({ ...f, status: v === "all" ? "" : v }))}>
            <SelectTrigger data-testid="filter-status" className="w-[130px] h-8 text-sm">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {["New", "Open", "In Progress", "Waiting", "Completed"].map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.priority} onValueChange={v => setFilters(f => ({ ...f, priority: v === "all" ? "" : v }))}>
            <SelectTrigger data-testid="filter-priority" className="w-[130px] h-8 text-sm">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priority</SelectItem>
              {["Low", "Normal", "High", "Urgent"].map(p => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.owner_id} onValueChange={v => setFilters(f => ({ ...f, owner_id: v === "all" ? "" : v }))}>
            <SelectTrigger data-testid="filter-owner" className="w-[140px] h-8 text-sm">
              <SelectValue placeholder="Owner" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Owners</SelectItem>
              {users.filter(u => u.status === "active").map(u => (
                <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            data-testid="filter-my-tasks"
            variant={filters.my_tasks ? "default" : "outline"}
            size="sm"
            className="h-8 text-xs"
            onClick={() => setFilters(f => ({ ...f, my_tasks: !f.my_tasks }))}
          >
            My Tasks
          </Button>
          {hasFilters && (
            <Button data-testid="filter-reset" variant="ghost" size="sm" className="h-8 text-xs" onClick={resetFilters}>
              <X className="w-3 h-3 mr-1" /> Reset
            </Button>
          )}
          {/* View toggle */}
          <div className="ml-auto flex items-center border border-[#EBEBEB] rounded-md overflow-hidden">
            <button
              data-testid="view-list-btn"
              onClick={() => changeViewMode("list")}
              className={`flex items-center gap-1 px-2.5 py-1.5 text-xs transition-colors ${
                viewMode === "list" ? "bg-[#111216] text-white" : "bg-white text-[#686B74] hover:bg-[#F4F5F8]"
              }`}
            >
              <List className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">List</span>
            </button>
            <button
              data-testid="view-card-btn"
              onClick={() => changeViewMode("card")}
              className={`flex items-center gap-1 px-2.5 py-1.5 text-xs transition-colors ${
                viewMode === "card" ? "bg-[#111216] text-white" : "bg-white text-[#686B74] hover:bg-[#F4F5F8]"
              }`}
            >
              <LayoutGrid className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Board</span>
            </button>
          </div>
        </div>

        {/* LIST VIEW */}
        {viewMode === "list" && (
          <div className="bg-white border border-[#EBEBEB] rounded-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#EBEBEB] bg-[#F9F9FB]">
                    <th className="text-left px-4 py-2 font-medium text-[#686B74] text-xs">ID</th>
                    <th className="text-left px-4 py-2 font-medium text-[#686B74] text-xs cursor-pointer" onClick={() => toggleSort("title")}>
                      <span className="flex items-center gap-1">Title <ArrowUpDown className="w-3 h-3" /></span>
                    </th>
                    <th className="text-left px-4 py-2 font-medium text-[#686B74] text-xs">Owner</th>
                    <th className="text-left px-4 py-2 font-medium text-[#686B74] text-xs">Group</th>
                    <th className="text-left px-4 py-2 font-medium text-[#686B74] text-xs">Status</th>
                    <th className="text-left px-4 py-2 font-medium text-[#686B74] text-xs">Priority</th>
                    <th className="text-left px-4 py-2 font-medium text-[#686B74] text-xs">Flag</th>
                    <th className="text-left px-4 py-2 font-medium text-[#686B74] text-xs cursor-pointer" onClick={() => toggleSort("due_date")}>
                      <span className="flex items-center gap-1">Due Date <ArrowUpDown className="w-3 h-3" /></span>
                    </th>
                    <th className="text-left px-4 py-2 font-medium text-[#686B74] text-xs cursor-pointer" onClick={() => toggleSort("created_at")}>
                      <span className="flex items-center gap-1">Created <ArrowUpDown className="w-3 h-3" /></span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((t, i) => (
                    <tr
                      key={t.id}
                      data-testid={`task-row-${i}`}
                      className="border-b border-[#EBEBEB] last:border-0 cursor-pointer transition-colors"
                      style={{ backgroundColor: getCardBg(t) }}
                      onMouseEnter={e => e.currentTarget.style.filter = "brightness(0.97)"}
                      onMouseLeave={e => e.currentTarget.style.filter = ""}
                      onClick={() => navigate(`/tasks/${t.id}`)}
                    >
                      <td className="px-4 py-2 text-xs text-[#686B74] font-mono">{t.task_number}</td>
                      <td className="px-4 py-2 font-medium text-[#111216] max-w-[250px] truncate">{t.title}</td>
                      <td className="px-4 py-2 text-[#686B74]">{t.owner_name}</td>
                      <td className="px-4 py-2 text-[#686B74]">{t.group_name || "-"}</td>
                      <td className="px-4 py-2">
                        <Badge variant="outline" className={`text-[11px] font-medium ${STATUS_COLORS[t.status] || ""}`}>
                          {t.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-2">
                        <Badge variant="outline" className={`text-[11px] font-medium ${PRIORITY_COLORS[t.priority] || ""}`}>
                          {t.priority}
                        </Badge>
                      </td>
                      <td className="px-4 py-2">
                        {t.flag_name ? (
                          <span
                            className="text-[11px] font-medium px-2 py-0.5 rounded-full border whitespace-nowrap"
                            style={{ backgroundColor: t.flag_color + "20", color: t.flag_color, borderColor: t.flag_color + "40" }}
                          >
                            {t.flag_name}
                          </span>
                        ) : <span className="text-[11px] text-[#686B74]">-</span>}
                      </td>
                      <td className="px-4 py-2 text-xs text-[#686B74]">
                        {t.due_date ? format(new Date(t.due_date + "T00:00:00"), "MMM d, yyyy") : "-"}
                      </td>
                      <td className="px-4 py-2 text-xs text-[#686B74]">
                        {t.created_at ? format(new Date(t.created_at), "MMM d") : "-"}
                      </td>
                    </tr>
                  ))}
                  {tasks.length === 0 && (
                    <tr><td colSpan={9} className="px-4 py-12 text-center text-[#686B74]">No tasks found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* CARD / KANBAN VIEW */}
        {viewMode === "card" && (
          <div className="flex gap-0 overflow-x-auto pb-4" data-testid="kanban-board">
            {KANBAN_STATUSES.map((status, colIdx) => {
              const columnTasks = tasks.filter(t => t.status === status);
              const isOver = dragOverColumn === status;
              return (
                <div
                  key={status}
                  className="flex-shrink-0 flex"
                  data-testid={`kanban-column-${status.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <div
                    style={{ width: `${columnWidth}px` }}
                    className="px-1.5"
                    onDragOver={(e) => handleDragOver(e, status)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, status)}
                  >
                    {/* Column Header */}
                    <div className="flex items-center gap-2 mb-3 px-1">
                      <div className={`rounded-full ${KANBAN_HEADER_COLORS[status]}`} style={{ width: `${Math.max(8, 10 * scale)}px`, height: `${Math.max(8, 10 * scale)}px` }} />
                      <h3 className="font-semibold text-[#111216] uppercase tracking-wider" style={{ fontSize: `${scaledFont(12)}px` }}>{status}</h3>
                      <span className="text-[#686B74] bg-[#F4F5F8] rounded-full px-1.5 py-0.5 font-medium" style={{ fontSize: `${scaledFont(10)}px` }}>{columnTasks.length}</span>
                    </div>
                    {/* Cards */}
                    <div className={`space-y-2 min-h-[60px] rounded-md transition-colors ${isOver ? "bg-[#4353FF]/5 ring-2 ring-[#4353FF]/20 ring-dashed p-1" : "p-0"}`}>
                      {columnTasks.map((t) => (
                        <div
                          key={t.id}
                          data-testid={`kanban-card-${t.id}`}
                          draggable
                          onDragStart={(e) => handleDragStart(e, t)}
                          onDragEnd={handleDragEnd}
                          onClick={() => navigate(`/tasks/${t.id}`)}
                          className="border border-[#EBEBEB] rounded-md cursor-grab active:cursor-grabbing hover:shadow-sm hover:border-[#4353FF]/30 transition-all group"
                          style={{
                            backgroundColor: getCardBg(t),
                            padding: `${Math.max(6, 12 * scale)}px`,
                          }}
                        >
                          <div className="flex items-center gap-1.5 mb-1" style={{ marginBottom: `${Math.max(2, 6 * scale)}px` }}>
                            <span className="font-mono text-[#686B74]" style={{ fontSize: `${scaledFont(10)}px` }}>{t.task_number}</span>
                            <Badge variant="outline" className="font-medium" style={{ fontSize: `${scaledFont(10)}px`, padding: `${Math.max(1, 2 * scale)}px ${Math.max(3, 6 * scale)}px` }}>
                              {t.priority}
                            </Badge>
                          </div>
                          <p className="font-medium text-[#111216] group-hover:text-[#4353FF] transition-colors line-clamp-2" style={{ fontSize: `${scaledFont(14)}px`, marginBottom: `${Math.max(2, 8 * scale)}px`, lineHeight: 1.3 }}>
                            {t.title}
                          </p>
                          <div className="flex items-center justify-between text-[#686B74]" style={{ fontSize: `${scaledFont(11)}px` }}>
                            <span className="truncate" style={{ maxWidth: `${Math.max(60, columnWidth * 0.5)}px` }}>{t.owner_name}</span>
                            {t.due_date && (
                              <span className={`flex-shrink-0 ${isBefore(new Date(t.due_date + "T00:00:00"), startOfDay(new Date())) && t.status !== "Completed" ? "text-[#E24A4A] font-semibold" : ""}`}>
                                {format(new Date(t.due_date + "T00:00:00"), "MMM d")}
                              </span>
                            )}
                          </div>
                          {t.group_name && (
                            <div className="text-[#686B74] bg-white/60 rounded px-1.5 py-0.5 inline-block mt-1" style={{ fontSize: `${scaledFont(10)}px` }}>
                              {t.group_name}
                            </div>
                          )}
                          {t.flag_name && (
                            <span
                              className="font-medium rounded-full border inline-block mt-1"
                              style={{
                                fontSize: `${scaledFont(10)}px`,
                                padding: `${Math.max(1, 2 * scale)}px ${Math.max(4, 8 * scale)}px`,
                                backgroundColor: t.flag_color + "20",
                                color: t.flag_color,
                                borderColor: t.flag_color + "40",
                              }}
                            >
                              {t.flag_name}
                            </span>
                          )}
                        </div>
                      ))}
                      {columnTasks.length === 0 && (
                        <div className="text-center py-6 text-[#686B74] border border-dashed border-[#EBEBEB] rounded-md bg-[#F9F9FB]/50" style={{ fontSize: `${scaledFont(12)}px` }}>
                          No tasks
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Resize handle between columns */}
                  <div
                    data-testid={`resize-handle-${colIdx}`}
                    onMouseDown={handleResizeStart}
                    className="w-2 flex-shrink-0 cursor-col-resize flex items-start justify-center pt-2 group/handle hover:bg-[#4353FF]/10 transition-colors rounded"
                    title="Drag to resize columns"
                  >
                    <GripVertical className="w-3 h-3 text-[#EBEBEB] group-hover/handle:text-[#4353FF] transition-colors" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Task Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-lg" data-testid="create-task-dialog">
          <DialogHeader>
            <DialogTitle>Create Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-[#686B74] mb-1 block">Title *</label>
              <Input data-testid="create-task-title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Task title" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#686B74] mb-1 block">Description</label>
              <Textarea data-testid="create-task-description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Describe the task..." rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-[#686B74] mb-1 block">Owner</label>
                <Select value={form.owner_id} onValueChange={v => setForm(f => ({ ...f, owner_id: v }))}>
                  <SelectTrigger data-testid="create-task-owner"><SelectValue placeholder="Assign owner" /></SelectTrigger>
                  <SelectContent>
                    {users.filter(u => u.status === "active").map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-[#686B74] mb-1 block">Group</label>
                <Select value={form.group_id} onValueChange={v => setForm(f => ({ ...f, group_id: v }))}>
                  <SelectTrigger data-testid="create-task-group"><SelectValue placeholder="Assign group" /></SelectTrigger>
                  <SelectContent>
                    {groups.map(g => (
                      <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-[#686B74] mb-1 block">Due Date</label>
                <Input data-testid="create-task-due-date" type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs font-medium text-[#686B74] mb-1 block">Priority</label>
                <Select value={form.priority} onValueChange={v => setForm(f => ({ ...f, priority: v }))}>
                  <SelectTrigger data-testid="create-task-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Low", "Normal", "High", "Urgent"].map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {flags.length > 0 && (
              <div>
                <label className="text-xs font-medium text-[#686B74] mb-1 block">Flag</label>
                <Select value={form.flag_id || "none"} onValueChange={v => setForm(f => ({ ...f, flag_id: v === "none" ? "" : v }))}>
                  <SelectTrigger data-testid="create-task-flag"><SelectValue placeholder="No flag" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No flag</SelectItem>
                    {flags.map(f => (
                      <SelectItem key={f.id} value={f.id}>
                        <span className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: f.color }} />
                          {f.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button data-testid="create-task-submit" onClick={handleCreate} className="bg-[#111216] hover:bg-[#2B2D31] text-white">Create Task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
